import { computed, reactive, toValue, watch } from "vue";
import { cloneDeep, reduce, uniq, without } from "lodash-es";

import { i18n } from "@/i18n";

import {
  commonOrDefault,
  mapValues
} from "@/helpers/utility/CollectionFunctions";
import { ensureInt, notNull } from "@/helpers/utility/Type";
import { formatRoundName } from "../simulationRound/useRoundTime";
import { createFilledArray, applyOrder } from "@/helpers/utility/Array";
import createSharedStatefulComposable from "@/helpers/utility/createSharedStatefulComposable";

import {
  defaultChartSettings,
  defaultData,
  defaultDataToggles,
  defaultItemConfigs
} from "@editor/items/charts/ChartItem";
import { charts } from "./charts";
import { getVirtualStartRound } from "../simulation/simulationFunctions";

import SimulationRound from "../simulationRound/SimulationRound";

import { useModelStore } from "../model/model.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useSimulationStore } from "../simulation/simulations.store";
import { useActionStore } from "../action/actions.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useIncidentStore } from "../incident/incidents.store";
import { useIndexStore } from "../index/indices.store";
import { useFeatures } from "@editor/store/features.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useSimulatedRoundStore } from "@editor/views/simulation/simulationVisualizer";

import {
  ACTIVE_SIMULATION_ID,
  VIRTUAL_END_ROUND_INDEX,
  VIRTUAL_START_ROUND_INDEX,
} from "./ChartItem";
import { fillChartWithDefaults } from "./chartGetters";

import type {
  ChartItem,
  ChartSettings,
  ChartType,
  ItemForChart,
  PartialChartItem,
  SelectableChartItem,
  SelectableChartItemIds,
  SelectableChartItemType
} from "@editor/items/charts/ChartItem";
import type { SimulationItem } from "../simulation/SimulationItem";
import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";
import type {
  IncidentActivationItem,
  ActionActivationItem,
  ScheduledEffectActivationItem
} from "./ChartDataTypes";
import type { IndexItem } from "../index/IndexItem";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { SetRequired } from "type-fest";
import type { MaybeRefOrGetter } from "vue";
import type { ChartAxisOption, ChartConfig } from "./charts";

type ChartItemContent = {
  uid: string;
  id?: number;
  chartType: ChartType | null;
  chartSettings: ChartSettings;
  itemConfigs: ChartItem["itemConfigs"];
  data: ChartItem["data"];
  dataToggles: ChartItem["dataToggles"];
  isDefaultSimulation?: boolean;
};

/**
 * Options used to create or manage a chart item.
 * Requires data and uid to be set.
 */
export type ChartItemOptions = SetRequired<PartialChartItem, "data"> & { uid: string; };

/**
 * Composable to manage chart data and related computed properties.
 * @param chartItemOptions 
 * @returns 
 */
function useChartDataComputedRaw<
  T extends null | ChartItemOptions
>(chartItemOptions: MaybeRefOrGetter<T>) {
  const features = useFeatures();
  const simulationStore = useSimulationStore();
  const simulationViewStore = useSimulationViewStore();
  const visualization = useSimulatedRoundStore();
  const settingsStore = useSettingsStore();

  const chartItem = computed(() => {
    type Result = T extends null ? null : ChartItemContent;
    const item: T = toValue(chartItemOptions);
    if (item == null) return null as Result;
    return fillChartWithDefaults(item as ChartItemOptions) as Result;
  });

  const itemConfigs = computed(
    () => chartItem.value?.itemConfigs || defaultItemConfigs
  );
  const data = computed(() => chartItem.value?.data || defaultData);
  const dataToggles = computed(
    () => chartItem.value?.dataToggles || defaultDataToggles
  );
  const chartSettings = computed(
    () => {
      if (!chartItem.value?.chartSettings) return defaultChartSettings;
      return { ...chartItem.value.chartSettings };
    }
  );

  const chartConfig = computed((): ChartConfig | null => {
    if (!chartItem.value?.chartType) return null;
    return charts[chartItem.value.chartType];
  });

  const chartAxisConfig = computed((): ChartAxisOption | undefined => {
    if (!chartConfig.value) return undefined;
    return chartConfig.value.axisOptions.find(
      (o) =>
        o.x == chartSettings.value.xAxis && o.y == chartSettings.value.yAxis
    );
  });

  // ======
  // SIMULATIONS

  const usesActiveSimulation = computed(() => {
    if (chartItem.value == null) return false;
    return chartItem.value.isDefaultSimulation;
  });

  const activeSimulation = computed((): SimulationItem | null => {
    const openSimulationId = simulationViewStore.openSimulationId;
    if (openSimulationId == null) return null;
    return {
      ...simulationStore.items[openSimulationId],
      id: ACTIVE_SIMULATION_ID,
      uid: `simulation_${ACTIVE_SIMULATION_ID}`,
      title: i18n.t("labels.openSimulation")
    };
  });

  const simulationIds = computed(() => {
    if (dataToggles.value.all.simulation) {
      return new Set(simulationStore.ids.slice(0));
    }

    const idsSet = new Set(data.value.simulation.map(ensureInt));

    if (usesActiveSimulation.value) {
      const openSimulationId = simulationViewStore.openSimulationId;
      if (openSimulationId != null) {
        idsSet.add(openSimulationId);
      }
    }

    return idsSet;
  });
  const simulationOrder = computed(() => {
    let ids = data.value.simulation.map(ensureInt);

    if (usesActiveSimulation.value) {
      const openSimulationId = simulationViewStore.openSimulationId;
      if (openSimulationId != null) {
        ids.unshift(openSimulationId);
      }
    }

    if (dataToggles.value.all.simulation) {
      const allIds = simulationStore.order.map(ensureNumber);
      ids.push(...allIds);

      // remove the virtual active simulation id if present,
      // as the active simulation is included in that set already.
      ids = without(ids, ACTIVE_SIMULATION_ID);
    }

    return uniq(ids);
  });

  /**
   * These are the simulation chosen if multiple simulations are selected.
   */
  const simulations = computed(() => {
    if (dataToggles.value.all.simulation) {
      return { ...simulationStore.items };
    }

    const result = simulationStore.getByIds(simulationIds.value);

    if (simulationIds.value.has(ACTIVE_SIMULATION_ID) && activeSimulation.value != null) {
      result[ACTIVE_SIMULATION_ID] = activeSimulation.value;
    }
    return result;
  });

  const orderedSimulations = computed(() => {
    return simulationOrder.value
      .map((id) => simulations.value[id])
      .filter(notNull);
  });

  /**
   * The main simulation used in the chart.
   * Only used when singleSimulation mode is active.
   */
  const selectedSimulation = computed((): SimulationItem | null => {
    if (!chartAxisConfig.value?.singleSimulation) return null;

    if (usesActiveSimulation.value) {
      const openSimulationId = simulationViewStore.openSimulationId;
      if (openSimulationId == null) return null;
      const item = simulationStore.items[openSimulationId];
      return item;
    }

    // take the fixed simulationId if the chart has one assigned
    const primarySimulationId = data.value.selectedSimulationId;
    if (primarySimulationId != null) {
      if (primarySimulationId == ACTIVE_SIMULATION_ID) return activeSimulation.value;
      const item = simulationStore.items[primarySimulationId];
      if (item) return item;
    }

    if (orderedSimulations.value.length > 0) {
      return orderedSimulations.value[0];
    }

    const allSimulations = simulationStore.orderedItems;

    if (allSimulations.length == 0) return null;

    return allSimulations[0];
  });

  const selectedSimulations = computed((): SimulationItem[] => {
    if (chartAxisConfig.value?.singleSimulation) {
      if (selectedSimulation.value) return [selectedSimulation.value];
      return [];
    }
    return orderedSimulations.value;
  });
  const selectedSimulationsById = computed(() => {
    if (chartAxisConfig.value?.singleSimulation) {
      if (selectedSimulation.value)
        return { [selectedSimulation.value.id]: selectedSimulation.value };
      return {};
    }
    return simulations.value;
  });

  const activeSimulationPlaceholder = computed(() => {
    return {
      type: "simulation" as const,
      id: ACTIVE_SIMULATION_ID,
      uid: `simulation_${ACTIVE_SIMULATION_ID}`,
      title: i18n.t("labels.openSimulation"),
    };
  });

  const unchosenSimulations = computed(() => {
    if (dataToggles.value.all.simulation) return [];

    const sims: { id: number; type: "simulation", uid: string, title: string }[] = [...simulationStore.orderedItems];

    if (!chartItem.value?.isDefaultSimulation) {
      sims.unshift(activeSimulationPlaceholder.value);
    }

    return sims.filter(
      (sim) => !simulationIds.value.has(sim.id)
    );
  });

  const commonRoundSettings = computed(() => {
    const roundUnit = commonOrDefault(
      selectedSimulations.value,
      (sim: SimulationItem) => sim.roundUnit,
      "round"
    );
    const durationType = commonOrDefault(
      selectedSimulations.value,
      (sim: SimulationItem) => sim.durationType,
      "roundAmount"
    );
    const roundLength = commonOrDefault(
      selectedSimulations.value,
      (sim: SimulationItem) => sim.roundLength,
      1
    );
    const timeStart = commonOrDefault(
      selectedSimulations.value,
      (sim: SimulationItem) => sim.timeStart,
      null
    );

    return {
      roundUnit,
      durationType,
      roundLength,
      timeStart
    };
  });

  // ==========
  // TARGETS

  /**
   * all target ids used in the selected simulations
   */
  const allAvailableTargets = computed(() => {
    return useTargetStore().items;
  });
  const allTargetsOrder = computed<number[]>(() => {
    const ids = data.value.target.map(ensureInt);

    const allIds = useTargetStore().order.map(ensureNumber);
    ids.push(...allIds);
    return Array.from(new Set(ids));
  });
  const targets = computed(() => {
    if (dataToggles.value.all.target) return allAvailableTargets.value;

    const targetById: { [targetId: number]: TargetItem } = {};
    const ids = data.value.target.map(ensureInt);
    for (const id of ids) {
      const target = allAvailableTargets.value[id];
      if (target) {
        targetById[id] = target;
      }
    }
    return targetById;
  });
  const selectedTargetsOrder = computed<number[]>(() => {
    if (dataToggles.value.all.target) {
      return allTargetsOrder.value;
    }

    return data.value.target.map(ensureInt);
  });
  const orderedTargets = computed(() => {
    return applyOrder(targets.value, selectedTargetsOrder.value);
  });

  // ===========
  // RESOURCES

  /**
   * all resource ids used in the selected simulations
   */
  const allAvailableResources = computed(() => {
    return useResourceStore().items;
  });
  const allResourcesOrder = computed<number[]>(() => {
    const ids = data.value.resource.map(ensureInt);
    const allIds = useResourceStore().order.map(ensureInt);
    ids.push(...allIds);
    return Array.from(new Set(ids));
  });
  const resources = computed(() => {
    if (dataToggles.value.all.resource) return allAvailableResources.value;

    const resourceById: { [resourceId: number]: ResourceItem } = {};
    const ids = data.value.resource.map(ensureInt);
    for (const id of ids) {
      const resource = allAvailableResources.value[id];
      if (resource) {
        resourceById[id] = resource;
      }
    }
    return resourceById;
  });
  const selectedResourcesOrder = computed<number[]>(() => {
    if (dataToggles.value.all.resource) {
      return allResourcesOrder.value;
    }
    return data.value.resource.map(ensureInt);
  });
  const orderedResources = computed(() => {
    return applyOrder(resources.value, selectedResourcesOrder.value);
  });

  // ===========
  // INDICES

  /**
   * all index ids used in the selected simulations
   */
  const allAvailableIndices = computed(() => {
    return useIndexStore().items;
  });
  const allIndicesOrder = computed<number[]>(() => {
    const ids = data.value.index.map(ensureInt);
    const allIds = useIndexStore().order.map(ensureInt);
    ids.push(...allIds);
    return Array.from(new Set(ids));
  });
  const selectedIndicesOrder = computed<number[]>(() => {
    if (dataToggles.value.all.index) {
      return allIndicesOrder.value;
    }
    return data.value.index.map(ensureInt);
  });

  const indices = computed(() => {
    if (dataToggles.value.all.index) return allAvailableIndices.value;

    const indexById: { [indexId: number]: IndexItem } = {};
    const ids = data.value.index.map(ensureInt);
    for (const id of ids) {
      const index = allAvailableIndices.value[id];
      if (index) {
        indexById[id] = index;
      }
    }
    return indexById;
  });

  const orderedIndices = computed(() => {
    return applyOrder(indices.value, selectedIndicesOrder.value);
  });

  // ========
  // ROUNDS

  const maxRoundIndex = computed(() => {
    return reduce(
      selectedSimulations.value,
      (max, sim) => Math.max(sim.rounds.length, max),
      settingsStore.simulationRoundAmount || 0
    );
  });

  const allRoundOptions = computed(() => {
    const options = [];
    const roundMode = chartAxisConfig.value?.roundMode;
    const min =
      roundMode == "single" || roundMode == "list" || roundMode == "pair"
        ? VIRTUAL_START_ROUND_INDEX
        : 0;
    for (let i = min; i < maxRoundIndex.value; i++) {
      options.push({
        index: i
      });
    }

    // Add virtual end round option
    options.push({
      index: VIRTUAL_END_ROUND_INDEX
    });

    return options;
  });

  const simulationRounds = computed(() => {
    return mapValues(selectedSimulationsById.value, (simulationItem) => {
      const rounds = simulationItem.rounds.map((r) => new SimulationRound(r));

      // Add live simulation data if applicable
      if (simulationViewStore.visualizing && (simulationItem.id == -1 || simulationViewStore.openSimulationId === simulationItem.id)) {
        const selectedRoundIndex = simulationViewStore.selectedRoundIndex;
        const activeRoundIndex = rounds.findIndex((r) => r.index === selectedRoundIndex);
        if (activeRoundIndex !== -1) {
          const newRound = new SimulationRound({
            ...cloneDeep(rounds[activeRoundIndex]),
            elementValues: visualization.elementValues,
            impulses: visualization.allImpulses,
          });
          rounds[activeRoundIndex] = newRound;
        }
      }

      return rounds;
    });
  });

  /**
   * Main getter for rounds in the selected simulations,
   * filtered by the chart round settings.
   * Each round also contains the previous round for comparison.
   * Used by mapChartData function!
   */
  const playedRoundsInRange = computed(() => {
    const roundList = data.value.simulationRound.map(ensureInt);
    const allRounds = dataToggles.value.all.simulationRound;
    const minRoundIndex = (Math.min(...roundList.filter((i) => i != VIRTUAL_END_ROUND_INDEX)) || 0) + 1;
    const roundMode = chartAxisConfig.value?.roundMode;

    return mapValues(selectedSimulationsById.value, (simulationItem) => {
      if (roundMode == null) return [];

      const simId = simulationItem.id;
      const rounds = (simulationRounds.value[simId] || []).filter((r) => r.played);

      const startRound = getVirtualStartRound(simulationItem);
      const roundsWithStart: SimulationRound[] = [startRound, ...rounds];

      if (allRounds) {
        if (roundMode == "single" || roundMode == "list") {
          return roundsWithStart.slice(-1).map((r) => withPreviousRound(r));
        }

        if (roundMode == "pair") {
          return [
            withPreviousRound(
              roundsWithStart[roundsWithStart.length - 1],
              roundsWithStart[0]
            )
          ];
        }

        return roundsWithStart
          .map((round, index, all) => {
            return withPreviousRound(round, all?.[index - 1]);
          })
          .slice(1);
      }

      if (roundMode == "single") {
        let index = roundList[0];
        if (index == VIRTUAL_END_ROUND_INDEX) {
          index = rounds.length - 1;
        }
        const round = roundsWithStart[index + 1];
        if (!round) return [];
        return [withPreviousRound(round)];
      }

      if (roundMode == "list") {
        return roundList
          .map((index) => {
            if (index == VIRTUAL_END_ROUND_INDEX) {
              const round = roundsWithStart[roundsWithStart.length - 1];
              if (!round) return null;
              const roundClone = new SimulationRound(round);
              const preparedRound = withPreviousRound(roundClone);
              preparedRound.index = VIRTUAL_END_ROUND_INDEX;
              return preparedRound;
            }
            const round = roundsWithStart[index + 1];
            if (!round) return null;
            return withPreviousRound(round);
          })
          .filter(notNull);
      }

      // Translate virtual end round index
      const endRoundIdIndex = roundList.indexOf(VIRTUAL_END_ROUND_INDEX);
      if (endRoundIdIndex !== -1) {
        roundList[endRoundIdIndex] = rounds.length - 1;
      }

      const maxRoundIndex: number = Math.min(
        roundList.length > 0 ? Math.max(...roundList) + 1 : rounds.length - 1,
        roundsWithStart.length - 1
      );

      if (roundMode == "pair") {
        const end = roundsWithStart[maxRoundIndex];
        const start = roundsWithStart[minRoundIndex];
        if (!end) return [withPreviousRound(start)];
        return [withPreviousRound(end, start)];
      }

      // roundMode: "range"
      return roundsWithStart
        .map((round, index, all) => {
          return withPreviousRound(round, all?.[index - 1]);
        })
        .slice(minRoundIndex, maxRoundIndex + 1);
    });
  });

  const roundsInRange = computed(() => {
    const allRounds = dataToggles.value.all.simulationRound;
    if (allRounds) {
      return playedRoundsInRange.value;
    }

    let roundList = data.value.simulationRound.map(ensureInt);
    const minRoundIndex = (Math.min(...roundList.filter((i) => i != VIRTUAL_END_ROUND_INDEX)) || 0) + 1;
    const roundMode = chartAxisConfig.value?.roundMode;

    return mapValues(selectedSimulationsById.value, (simulationItem) => {
      if (roundMode == null) return [];
      const rounds = simulationRounds.value[simulationItem.id] || [];

      const startRound = getVirtualStartRound(simulationItem);
      const roundsWithStart: SimulationRound[] = [startRound, ...rounds];

      if (roundMode == "single") {
        let index = roundList[0];
        if (index == VIRTUAL_END_ROUND_INDEX) {
          index = rounds.length - 1;
        }
        const round = roundsWithStart[index + 1];
        if (!round) return [];
        return [withPreviousRound(round)];
      }

      if (roundMode == "list") {
        return roundList
          .map((index) => {
            if (index == VIRTUAL_END_ROUND_INDEX) {
              const round = roundsWithStart[roundsWithStart.length - 1];
              if (!round) return null;
              const roundClone = new SimulationRound(round);
              const preparedRound = withPreviousRound(roundClone);
              preparedRound.index = VIRTUAL_END_ROUND_INDEX;
              return preparedRound;
            }
            const round = roundsWithStart[index + 1];
            if (!round) return null;
            return withPreviousRound(round);
          })
          .filter(notNull);
      }

      // Translate virtual end round index
      const endRoundIdIndex = roundList.indexOf(VIRTUAL_END_ROUND_INDEX);
      if (endRoundIdIndex !== -1) {
        roundList[endRoundIdIndex] = rounds.length - 1;
        // Any rounds beyond played rounds are removed
        roundList = roundList.filter((i) => i <= rounds.length - 1);
      }

      const maxRoundIndex: number = roundList.length > 0 ? Math.max(...roundList) + 1 : rounds.length - 1;

      if (roundMode == "pair") {
        let maxAvailableIndex = roundsWithStart.filter((round) => round.played).length - 1;
        const end = roundsWithStart[Math.min(maxRoundIndex, maxAvailableIndex)];
        const start = roundsWithStart[minRoundIndex];
        if (!end && !start) return [
          withPreviousRound(new SimulationRound({ index: minRoundIndex - 1, played: false }))
        ];
        if (!end) return [withPreviousRound(start)];
        return [withPreviousRound(end, start)];
      }

      // roundMode: "range"
      const filledRoundList = createFilledArray(maxRoundIndex + 1).slice(minRoundIndex, maxRoundIndex + 1);
      return filledRoundList.map((i) => {
        const round = roundsWithStart[i] || new SimulationRound({
          index: i - 1,
          played: false,
        });
        const previousRound = roundsWithStart[i - 1] || new SimulationRound({
          index: i - 2,
          played: false,
        });
        return withPreviousRound(round, previousRound);
      });
    });
  });

  const maxCompletedRounds = computed(() => {
    const playedRounds = playedRoundsInRange.value;
    return reduce(
      playedRounds,
      (max, rounds) => Math.max(rounds.length, max),
      0
    );
  });

  const selectedSimulationRounds = computed(() => {
    if (!selectedSimulation.value) return {};
    const rounds = playedRoundsInRange.value[selectedSimulation.value.id];
    // order rounds by index
    const roundObj: { [roundIndex: number]: SimulationRound } = {};
    for (const round of rounds) {
      roundObj[round.index] = round;
    }
    return roundObj;
  });

  // =========
  // ACTIONS

  // Actions are added by simulation - a action can
  // be played multiple times by simulation, so
  // we're not adding them by action id alone, but
  // with a composed action id in the simulation.
  const availableActions = computed(() => {
    if (!selectedSimulation.value) return {};
    const simulation = selectedSimulation.value;

    // Get the actions of ALL rounds.
    // Even actions that were played in previous rounds
    // can have an effect on following rounds through
    // indirect effects.
    const rounds = simulation.rounds.filter((r) => r.played);

    const actionActivations: {
      [id: string]: ActionActivationItem;
    } = {};

    for (const roundIndex in rounds) {
      const round = rounds[roundIndex];

      const activatedActions = getRoundActivatedActions(simulation, round);
      for (const actionActivation of activatedActions) {
        actionActivations[actionActivation.id] = actionActivation;
      }
    }
    return actionActivations;
  });

  const actionActivations = computed(() => {
    const actionActivationById: {
      [id: string]: ActionActivationItem;
    } = {};
    for (const id of data.value.actionActivation) {
      if (availableItems.value.actionActivation[id]) {
        const activation = availableItems.value.actionActivation[id];
        if (activation) {
          actionActivationById[id] = activation;
        }
      }
    }
    return actionActivationById;
  });
  const actionActivationOrder = computed(() => {
    return data.value.actionActivation;
  });
  const orderedActionActivations = computed(() => {
    return applyOrder(actionActivations.value, actionActivationOrder.value);
  });

  // =========
  // INCIDENTS

  // Incidents are added by simulation - an incident can
  // be triggered multiple times by simulation, so
  // we're not adding them by incident id alone, but
  // with a composed incident id in the simulation.
  const availableIncidents = computed(() => {
    if (!selectedSimulation.value) return {};
    const simulation = selectedSimulation.value;

    // Get the incidents of ALL rounds.
    // Even incidents that were played in previous rounds
    // can have an effect on following rounds through
    // indirect effects.
    const rounds = simulation.rounds.filter((r) => r.played);

    const incidentActivations: {
      [id: string]: IncidentActivationItem;
    } = {};

    for (const roundIndex in rounds) {
      const round = rounds[roundIndex];

      const activatedIncidents = getRoundActivatedIncidents(simulation, round);
      for (const incidentActivation of activatedIncidents) {
        incidentActivations[incidentActivation.id] = incidentActivation;
      }
    }
    return incidentActivations;
  });

  const incidentActivations = computed(() => {
    const incidentActivationById: {
      [id: string]: IncidentActivationItem;
    } = {};
    for (const id of data.value.incidentActivation) {
      const activation = availableItems.value.incidentActivation[id];
      if (activation) {
        incidentActivationById[id] = activation;
      }
    }
    return incidentActivationById;
  });
  const incidentActivationOrder = computed(() => {
    return data.value.incidentActivation;
  });
  const orderedIncidentActivations = computed(() => {
    return applyOrder(incidentActivations.value, incidentActivationOrder.value);
  });

  // =========
  // SCHEDULED EFFECTS

  // Scheduled Effects are added by simulation - an effect can
  // be played multiple times by simulation.
  const availableScheduledEffects = computed(() => {
    if (!selectedSimulation.value) return {};
    const rounds = selectedSimulationRounds.value;
    const simulation = selectedSimulation.value;

    const scheduledEffectActivations: {
      [id: string]: ScheduledEffectActivationItem;
    } = {};

    for (const roundIndex in rounds) {
      const round = rounds[roundIndex];

      const activatedEffects = getRoundActivatedScheduledEffects(
        simulation,
        round
      );
      for (const effectActivation of activatedEffects) {
        scheduledEffectActivations[effectActivation.id] = effectActivation;
      }
    }
    return scheduledEffectActivations;
  });

  const scheduledEffectActivations = computed(() => {
    const scheduledEffectActivationById: {
      [id: string]: ScheduledEffectActivationItem;
    } = {};
    for (const id of data.value.scheduledEffectActivation) {
      const activation = availableItems.value.scheduledEffectActivation[id];
      if (activation) {
        scheduledEffectActivationById[id] = activation;
      }
    }
    return scheduledEffectActivationById;
  });
  const scheduledEffectActivationOrder = computed(() => {
    return data.value.scheduledEffectActivation;
  });
  const orderedScheduledEffectActivations = computed(() => {
    return applyOrder(
      scheduledEffectActivations.value,
      scheduledEffectActivationOrder.value
    );
  });

  // =======
  // ITEMS

  /**
   * All available items for the chart.
   */
  const availableItems = computed(() => {
    const items: {
      [Type in SelectableChartItemType]: Record<string, ItemForChart<Type>>;
    } = {
      simulation: simulationStore.items as Record<string, SimulationItem>,
      target: allAvailableTargets.value as Record<string, TargetItem>,
      resource: allAvailableResources.value as Record<string, ResourceItem>,
      index: allAvailableIndices.value as Record<string, IndexItem>,
      simulationRound: selectedSimulationRounds.value as Record<
        string,
        SimulationRoundItem
      >,
      actionActivation: availableActions.value as Record<
        string,
        ActionActivationItem
      >,
      incidentActivation: availableIncidents.value as Record<
        string,
        IncidentActivationItem
      >,
      scheduledEffectActivation: availableScheduledEffects.value as Record<
        string,
        ScheduledEffectActivationItem
      >
    };
    return items;
  });

  const orderedAvailableItems = computed(() => {
    const items = availableItems.value;
    return {
      simulation: applyOrder(items.simulation, getOrder("simulation")),
      target: applyOrder(items.target, getOrder("target")),
      resource: applyOrder(items.resource, getOrder("resource")),
      index: applyOrder(items.index, getOrder("index")),
      simulationRound: applyOrder(
        items.simulationRound,
        getOrder("simulationRound")
      ),
      incidentActivation: Object.values(items.incidentActivation),
      actionActivation: Object.values(items.actionActivation),
      scheduledEffectActivation: Object.values(items.scheduledEffectActivation)
    };
  });

  const itemOrders = reactive({
    target: selectedTargetsOrder,
    resource: selectedResourcesOrder,
    index: selectedIndicesOrder,
    actionActivation: actionActivationOrder,
    incidentActivation: incidentActivationOrder,
    scheduledEffectActivation: scheduledEffectActivationOrder,
    simulation: simulationOrder
  });

  const selectedItem = computed((): SelectableChartItem | null => {
    if (!chartAxisConfig.value?.singleItem) return null;

    const chartDataMap = data.value;
    const itemData = chartDataMap.selectedItem;

    // if the primary type is valid, return that.
    if (itemData && validItemTypes.value.includes(itemData.type)) {
      const type = itemData.type;

      const allItemsOfType = availableItems.value[type];

      const item = allItemsOfType?.[itemData.id] ?? null;

      if (item) return item;
    }

    // get the first available item for any of the valid types
    for (const type of validItemTypes.value) {
      const allItemsOfType = availableItems.value[type] ?? {};
      const keys = Object.keys(allItemsOfType);
      return allItemsOfType[keys[0]] ?? null;
    }

    return null;
  });

  const selectedItems = computed(() => {
    if (selectedItem.value != null) return [selectedItem.value];

    const items = [];

    for (const type of validItemTypes.value) {
      if (type == "target") {
        items.push(...orderedTargets.value);
      } else if (type == "resource") {
        items.push(...orderedResources.value);
      } else if (type == "index") {
        items.push(...orderedIndices.value);
      } else if (type == "actionActivation") {
        items.push(...orderedActionActivations.value);
      } else if (type == "incidentActivation") {
        items.push(...orderedIncidentActivations.value);
      } else if (type == "scheduledEffectActivation") {
        items.push(...orderedScheduledEffectActivations.value);
      }
    }

    return items;
  });

  /**
   * All items that will be used in the chart
   */
  const chartItems = computed(() => {
    const cAxisConfig = chartAxisConfig.value;
    if (!cAxisConfig) return [];
    if (cAxisConfig?.singleItem) {
      if (selectedItem.value) return [selectedItem.value];
      return [];
    }
    const items = [];

    if (cAxisConfig.item.includes("target")) {
      items.push(...orderedTargets.value);
    }

    if (cAxisConfig.item.includes("resource")) {
      items.push(...orderedResources.value);
    }

    return items;
  });

  /**
   * Predefined settings by the current chart configuration
   */
  const preSettings = computed(() => {
    const preSettingsConfig = chartAxisConfig.value?.preSettings ?? {};
    const preSettingsKey =
      selectedItem.value?.type ?? chartSettings.value.xAxis;
    const preSettings = Object.assign(
      {},
      preSettingsConfig?.all ?? {},
      preSettingsConfig?.[preSettingsKey] ?? {}
    );
    return preSettings;
  });

  const availableSettings = computed(() => {
    const settings = new Set(chartConfig.value?.availableChartSettings || []);
    for (const setting of chartAxisConfig.value?.extraSettings || []) {
      settings.add(setting);
    }
    return settings;
  });

  const availableSettingsValues = computed(
    (): Partial<ChartSettings> & Pick<ChartSettings, "xAxis" | "yAxis"> => {
      const settings: ChartSettings = {
        ...chartSettings.value,
        ...preSettings.value
      };

      for (const _key in settings) {
        const key = _key as keyof ChartSettings;

        // always keep the axes
        if (key == "xAxis") continue;
        if (key == "yAxis") continue;

        // include all presettings!
        if (key in preSettings.value) continue;

        if (!availableSettings.value.has(key)) {
          delete settings[key];
        }
      }

      return settings;
    }
  );

  const validItemTypes = computed(() => {
    let types = chartAxisConfig.value?.item ?? [];

    // filter out types that are not available
    if (!features.target) types = types.filter((t) => t != "target");
    if (!features.resource) types = types.filter((t) => t != "resource");
    if (!features.index) types = types.filter((t) => t != "index");
    if (!features.action)
      types = types.filter((t) => t != "actionActivation");
    if (!features.incident)
      types = types.filter((t) => t != "incidentActivation");
    if (!features.scheduledEffect)
      types = types.filter((t) => t != "scheduledEffectActivation");

    return types;
  });

  /**
   * All items that are available for selection
   * grouped by item type
   */
  const itemOptions = computed(() => {
    const options: {
      [key in SelectableChartItemType]: (SelectableChartItem<key> & {
        title?: string;
      })[];
    } = { ...orderedAvailableItems.value };

    for (const type in options) {
      if (!validItemTypes.value.includes(type as SelectableChartItemType)) {
        delete options[type as SelectableChartItemType];
      }
    }

    return options;
  });

  /**
   * All items that are not yet selected
   * grouped by item type
   */
  const remainingItemOptions = computed(() => {
    const remainingOptions: typeof itemOptions.value = { ...itemOptions.value };
    const keys = Object.keys(remainingOptions) as SelectableChartItemType[];

    function filterItems<
      T extends SelectableChartItemType,
      R extends Record<SelectableChartItemType, unknown>
    >(type: T, result: R) {
      const ids = data.value[type] as SelectableChartItemIds[T][];
      const typeOptions = itemOptions.value[type];
      const opts = typeOptions.filter((option) => {
        const oid = option.id;
        return !ids.includes(oid);
      });
      result[type] = opts;
    }

    for (const type of keys) {
      filterItems(type, remainingOptions);
    }

    return remainingOptions;
  });

  watch(chartSettings, () => console.log("chartSettings changed"));

  return {
    chartItem,
    itemConfigs,
    data,
    dataToggles,

    chartConfig,
    chartAxisConfig,

    simulationIds,
    orderedSimulations,
    selectedSimulation,
    selectedSimulations,
    unchosenSimulations,
    simulationOrder,

    commonRoundSettings,

    targets,
    selectedTargetsOrder,
    orderedTargets,
    allAvailableTargets,
    allTargetsOrder,
    
    resources,
    selectedResourcesOrder,
    orderedResources,
    allAvailableResources,
    allResourcesOrder,

    indices,
    selectedIndicesOrder,
    orderedIndices,
    allAvailableIndices,
    allIndicesOrder,

    actionActivations,
    actionActivationOrder,

    incidentActivations,
    incidentActivationOrder,

    scheduledEffectActivations,
    scheduledEffectActivationOrder,

    availableSettings,
    availableSettingsValues,
    preSettings,

    allRoundOptions,
    playedRoundsInRange,
    roundsInRange,
    maxCompletedRounds,
    simulationRounds,

    chartItems,
    availableItems,
    orderedAvailableItems,
    itemOptions,
    remainingItemOptions,
    selectedItem,
    selectedItems,
    itemOrders
  };
}

/**
 * Shared composable to get the data for a chart.
 */
export const useChartData = createSharedStatefulComposable(
  useChartDataComputedRaw,
  (chartOptions): string => {
    if (chartOptions == null) return "null";
    const val = toValue(chartOptions);
    if (val == null) return "null";
    if (val.uid == null) return "no-uid";
    return val.uid;
  }
);

/**
 * Reactive version of useChartData
 * @param chartOptions Chart item content or ref/getter thereof
 */
export function useReactiveChartData<
  ID extends null | ChartItemOptions
>(chartOptions: MaybeRefOrGetter<ID>) {
  return reactive(useChartData(chartOptions));
}

// ===========================
// HELPERS

/**
 * Returns all activated actions of a round.
 */
export function getRoundActivatedActions(
  simulation: SimulationItem,
  round: SimulationRoundItem
) {
  const actionItems = useActionStore().items;
  if (!round.activatedActions) return [];
  return Array.from(round.activatedActions.entries()).reduce(
    (activations, [index, actionActivation]) => {
      const activationId = `${round.index}_${index}`;
      const action = actionItems[actionActivation.actionId];

      const roundName = formatRoundName(
        simulation.durationType,
        simulation.roundUnit,
        simulation.roundLength,
        round.index,
        simulation.timeStart
      );

      activations.push({
        ...action,
        id: activationId,
        uid: `actionActivation_${activationId}`,
        roundIndex: round.index,
        type: "actionActivation",
        itemType: "action",
        itemId: action.id,
        title: `${roundName} – ${action.title}`,
        simulationId: simulation.id,
        ...actionActivation
      });

      return activations;
    },
    [] as ActionActivationItem[]
  );
}

/**
 * Returns all activated incidents of a round.
 */
export function getRoundActivatedIncidents(
  simulation: SimulationItem,
  round: SimulationRoundItem
) {
  const incidentItems = useIncidentStore().items;
  return Array.from(round.activatedIncidents.entries()).reduce(
    (activations, [index, incidentActivation]) => {
      const activationId = `${round.index}_${index}`;
      const incident = incidentItems[incidentActivation.incidentId];

      const roundName = formatRoundName(
        simulation.durationType,
        simulation.roundUnit,
        simulation.roundLength,
        round.index,
        simulation.timeStart
      );

      activations.push({
        ...incident,
        id: activationId,
        roundIndex: round.index,
        type: "incidentActivation",
        itemType: "incident",
        itemId: incident.id,
        title: `${roundName} – ${incident.title}`,
        simulationId: simulation.id,
        ...incidentActivation
      });

      return activations;
    },
    [] as IncidentActivationItem[]
  );
}

/**
 * Returns all activated scheduled effects of a round.
 */
export function getRoundActivatedScheduledEffects(
  simulation: SimulationItem,
  round: SimulationRoundItem
) {
  const actionPlan = simulation.actionPlans[round.index];
  if (!actionPlan) return [];
  const scheduledEffectItems = actionPlan.scheduledEffects.slice();
  return scheduledEffectItems.reduce(
    (activations, scheduledEffect, index) => {
      const activationId = `${round.index}_${index}`;

      const affectedItem = useModelStore().getItem(
        scheduledEffect.affectedType,
        scheduledEffect.affectedId
      );

      const roundName = formatRoundName(
        simulation.durationType,
        simulation.roundUnit,
        simulation.roundLength,
        round.index,
        simulation.timeStart
      );

      activations.push({
        ...scheduledEffect,
        id: activationId,
        uid: `scheduledEffectActivation_${activationId}`,
        roundIndex: round.index,
        type: "scheduledEffectActivation",
        itemType: "scheduledEffect",
        itemId: scheduledEffect.id,
        title: `${roundName} – ${affectedItem?.title || ""}`,
        simulationId: simulation.id,
        impulseExpressions: scheduledEffect.impulseExpressions.slice()
      });

      return activations;
    },
    [] as ScheduledEffectActivationItem[]
  );
}


function withPreviousRound(
  round: SimulationRound,
  prevRound: SimulationRound | undefined = undefined
) {
  const newRound: SimulationRound & { previousRound?: SimulationRound } =
    new SimulationRound(round);

  newRound.previousRound = prevRound;

  return newRound;
}

export function getOrder<T extends SelectableChartItemType>(
  type: T
): SelectableChartItemIds[T][] | null {
  const _t: SelectableChartItemType = type;
  // Hack, we limit the rounds to 10000 rounds. That's not clean.
  if (_t == "simulationRound") {
    return createFilledArray(10000) as SelectableChartItemIds[T][];
  }

  if (_t == "actionActivation") return null;
  if (_t == "incidentActivation") return null;
  if (_t == "scheduledEffectActivation") return null;

  const store = useModelStore().storesByType[_t];
  return store.order as SelectableChartItemIds[T][];
}

function ensureNumber(k: string | number) {
  if (typeof k === "number") return k;
  return parseInt(k);
}
